import { useCallback } from 'react'
import { create } from 'zustand'

/**
 * 탭 전환 시에도 필터/기간/검색 등 화면 상태를 유지하기 위한 인메모리 스토어.
 *
 * 출고처리·입고처리의 하위 탭(전표별·거래처별·품목별·판매월보 등)은 각각 별도
 * 라우트 컴포넌트라, 탭을 옮기면 언마운트되어 useState 로 잡은 기간/검색/필터가
 * 초기화된다. 이 스토어에 (key, field) 로 보관하면 탭을 오가도 그대로 유지된다.
 *
 * key 는 탭 고유 식별자(예: config.queryKey='deliveries' / 'receivings',
 * queryKeyPrefix='partner-summary' 등)라 출고/입고가 자동으로 분리된다.
 *
 * localStorage 로 persist 하지 않는다 — 세션 내 탭 이동 동안만 유지되면 충분하고,
 * 새로고침 시 초기값으로 돌아가는 편이 자연스럽다.
 */

type Bag = Record<string, unknown>

interface TabStateStore {
  data: Record<string, Bag>
  setField: (key: string, field: string, value: unknown) => void
}

const useTabStateStore = create<TabStateStore>((set) => ({
  data: {},
  setField: (key, field, value) =>
    set((s) => ({
      data: { ...s.data, [key]: { ...(s.data[key] || {}), [field]: value } },
    })),
}))

/**
 * useState 드롭인 대체. (key, field) 로 스코프된 값을 스토어에 보관해 컴포넌트가
 * 언마운트돼도 값이 살아남는다.
 *
 *   const [dateFrom, setDateFrom] = useTabField(c.queryKey, 'dateFrom', firstOfMonthISO())
 *
 * 배열·객체·nullable 초기값은 제네릭을 명시할 것: useTabField<number[]>(key, 'ids', []).
 */
export function useTabField<T>(
  key: string, field: string, initial: T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const stored = useTabStateStore((s) => s.data[key]?.[field]) as T | undefined
  const setField = useTabStateStore((s) => s.setField)
  const value = stored === undefined ? initial : stored
  const setValue = useCallback(
    (v: T | ((prev: T) => T)) => {
      const next = typeof v === 'function' ? (v as (prev: T) => T)(value) : v
      setField(key, field, next)
    },
    [key, field, setField, value],
  )
  return [value, setValue]
}
